import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import t from 'react-translate';
import { pick } from 'lodash';
import { css } from '@emotion/core';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// redux
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse } from 'redux/selectors/course';
import { RootState } from 'redux/schemas';
import { updateLectureComponent } from 'redux/actions/lecture-pages';
import { wrapThunkAction } from 'redux/utils';
import { NQuizQuestion, QuizQuestionType, SubmissionResponse } from 'redux/schemas/models/quiz';
import { getQuizQuestions } from 'redux/selectors/quizzes';
import { resetNewComponent } from 'redux/actions/lecture-components';
import { getQuiz } from 'redux/selectors/timeline';
import { addQuizQuestion } from 'redux/actions/quizzes';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import BaseLectureComponentContext from 'lecture_pages/directives/components/base-lecture-component/context';
import { DeepPartial } from 'utility-types';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import { isEmpty } from 'underscore';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { AllQuestionViewQuizAttemptSettingModal } from 'quizzes/components/quiz-basic-setting-modal';
import QuizModal from 'quizzes/components/quiz-modal';
import { danger, gray6, hexToRgbaString } from 'styles/global_defaults/colors';
import { Button } from 'react-bootstrap';
import NvIcon from 'shared/components/nv-icon';
import { AllQuestionViewQuizFormStateType } from '../../../quizzes/components/context';
import { LectureComponentProps, LecturePageMode } from '..';
import QuizComponentTitleContent from '../quiz/shared/quiz-lecture-component-title-content';
import SubmitSection from './submit-section';
import AllQuestionViewQuestion from './all-question-view-question';
import { NovoAIItemType } from 'redux/schemas/models/lecture-page';

type QuizLectureComponentType = LectureComponent<ComponentType.QUIZ>;
type ForwardOnQuestionsModalCloseRefType = {
  callback: Function,
  restrictModalClose: boolean,
};
export enum SavingStatus {
  SAVING = 'saving',
  SAVED = 'saved',
  SAVE = 'save',
}
const QuizLectureComponent = (props: LectureComponentProps<ComponentType.QUIZ>) => {
  const {
    mode,
    currentLecture,
    lectureComponent,
  } = props;

  const styles = css`
    .questions-list {
      border-top: 1px solid ${gray6};
      .need-questions {
        background-color: ${hexToRgbaString(danger, 0.05)};
        border: 1px dashed ${danger};
      }
    }
  `;

  const quiz = useSelector((state) => getQuiz(state, lectureComponent.quiz));

  const questions: NQuizQuestion[] = useSelector(
    (state: RootState) => getQuizQuestions(state, quiz.id, true),
  );
  const { expirationDate } = quiz;

  const dispatch = useAppDispatch();
  const currentLectureId = currentLecture.id;
  const currentCourse = useSelector(getCurrentCourse);
  const isCurrentCourseSelfPaced = currentCourse.isSelfPaced;

  const quizContainerRef = useRef<HTMLDivElement>(null);
  const forwardOnQuestionsModalCloseRef = useRef<ForwardOnQuestionsModalCloseRefType>(
    {
      callback: () => {},
      restrictModalClose: false,
    },
  );

  const [showEditBasics, setShowEditBasics] = useState(false);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const [disableAddQuestion, setDisableAddQuestion] = useState(false);

  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);
  const isNewComponent = useSelector((state) => state.app.newComponent === lectureComponent?.id) ?? false;
  const [showEditQuestions, setShowEditQuestions] = useState(false);
  const [saveStatus, setSaveStatus] = useState(SavingStatus.SAVE);
  const lectureComponentId = lectureComponent.id;
  const isUnlimitedAttempts = quiz.maximumAttempts === 0 || !quiz.maximumAttempts;

  let defaultValues = {};

  // setting default values for quiz questions
  questions.forEach((eachQuestion) => {
    if (eachQuestion.displayIndex !== null) {
      switch (eachQuestion.type) {
        case QuizQuestionType.MULTIPLE_CHOICE:
        case QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER:
          defaultValues = { ...defaultValues, [eachQuestion.id]: null };
          break;
        default:
          defaultValues = { ...defaultValues, [eachQuestion.id]: '' };
          break;
      }
    }
  });

  // setting default value for table question
  questions.forEach((eachQuestion) => {
    if (eachQuestion?.childrenQuestions?.length > 0) {
      let initialTableAnswerState = {};

      eachQuestion.childrenQuestions.forEach((childQuestion, i) => {
        initialTableAnswerState = {
          ...initialTableAnswerState,
          [i]: (eachQuestion.type === QuizQuestionType.TABLE_NUMBER_QUESTION) ? null : '',
        };
      });
      defaultValues[eachQuestion.id] = { ...initialTableAnswerState };
    }
  });


  let validationShape = {};
  questions.forEach((eachQuestion) => {
    if (eachQuestion.type === QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION) {
      validationShape = { ...validationShape,
        [eachQuestion.id]: eachQuestion.isRequired ? Yup.string()
          .required(t.VALIDATION.REQUIRED())
          .max(255, t.VALIDATION.MAX('255')) : Yup.string()
          .max(255, t.VALIDATION.MAX('255')),
      };
    }
    if (eachQuestion.type === QuizQuestionType.NUMBER_QUESTION) {
      validationShape = { ...validationShape,
        [eachQuestion.id]: eachQuestion.isRequired ? Yup.number()
          .transform(value => (Number.isNaN(value) ? undefined : value))
          .required(t.VALIDATION.REQUIRED())
          .typeError(t.VALIDATION.NUMBER()) : Yup.number()
          .transform(value => (Number.isNaN(value) ? undefined : value))
          .typeError(t.VALIDATION.NUMBER()),
      };
    }
    if (eachQuestion.type === QuizQuestionType.LONG_ANSWER_QUESTION) {
      validationShape = { ...validationShape,
        [eachQuestion.id]: eachQuestion.isRequired ? Yup.string()
          .required(t.VALIDATION.REQUIRED())
          .max(16000, t.VALIDATION.CHARACTER_LIMIT_POPOVER_ERROR('16,000'))
          : Yup.string().max(16000, t.VALIDATION.CHARACTER_LIMIT_POPOVER_ERROR('16,000')),
      };
    }
  });

  const ValidationSchema = Yup.object().shape(validationShape);

  const form = useForm<AllQuestionViewQuizFormStateType>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(ValidationSchema),
  });

  const {
    setValue,
  } = form;

  useEffect(() => {
    // Open edit questions modal if the component is newly added,
    // but not when coming from AI suggested activities
    if (
      isNewComponent &&
      lectureComponent?.aiOrigin !== NovoAIItemType.SUGGEST_ACTIVITIES
    ) {
      console.log('Opening edit questions modal for new quiz component', lectureComponent);
      setShowEditQuestions(true);
      dispatch(resetNewComponent());
    }
  }, [dispatch, isNewComponent]);

  useEffect(() => {
    if (!isEmpty(quiz.unsubmittedSubmission)) {
      const unsubmittedSubmissionResponses: SubmissionResponse[] = quiz.unsubmittedSubmission.responses;
      unsubmittedSubmissionResponses.forEach((eachResponse: SubmissionResponse) => {
        if (eachResponse?.childrenQuestionResponses?.length > 0) {
          // if the response of questions are availbale as childrenQuestionResponses
          // eg: table questions
          let answerObject = {};
          eachResponse?.childrenQuestionResponses.forEach((eachChildResponse, index) => {
            answerObject = { ...answerObject,
              [index]: eachChildResponse.response,
            };
          });
          setValue(eachResponse.questionId.toString(), answerObject);
        } else {
          setValue(eachResponse.questionId.toString(), eachResponse.response);
        }
      });
      if (saveStatus === SavingStatus.SAVING) {
        setTimeout(() => {
          setSaveStatus(SavingStatus.SAVED);
        });
      }
    }
    /**
     * Removing saveStatus from dependency array because function need to trigger
     * only when unsubmittedSubmission changes
     */
  }, [quiz.unsubmittedSubmission, quiz.submission, setValue]);

  const { sharedProps, setSharedProps } = useContext(BaseLectureComponentContext);
  const initialSharedProps = useRef(sharedProps).current;

  const lecturePageId = useSelector((state: RootState) => state.app.lecturePage.currentLectureId);
  const course = useSelector((state) => getCurrentCourse(state));
  const lecturePage = useSelector(state => state.models.lecturePages[lecturePageId]);

  const openEditQuestions = useCallback(() => {
    setShowEditQuestions(true);
  }, []);

  const editBasics = () => setShowEditBasics(true);

  const saveComponent = React.useCallback((componentDataPatch: DeepPartial<QuizLectureComponentType['quiz']>) => {
    const componentDataUpdate = {
      catalogId: currentCatalogId,
      lecturePageId: currentLectureId,
      componentData: {
        id: lectureComponentId,
        type: lectureComponent.type,
        quiz: {
          ...pick(quiz, [
            'isTodo',
            'hardDeadline',
            'expirationDate',
            'maximumAttempts',
            'questionMaximumAttempts',
          ]),
          ...componentDataPatch,
        },
      },
    };

    return dispatch(updateLectureComponent(componentDataUpdate as any));
  }, [
    dispatch,
    quiz,
    currentLectureId,
    currentCatalogId,
    lectureComponentId,
    lectureComponent.type,
  ]);

  React.useEffect(() => {
    const options: NvDropdownOption[] = [{
      type: 'text',
      callback: editBasics,
      text: t.LECTURE_PAGES.COMPONENTS.DROPDOWN.EDIT_BASICS(),
      disabled: !!lecturePage.isLinked,
      tooltip: {
        enabled: !!lecturePage.isLinked,
        text: t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP(),
        placement: 'left',
        offset: 20,
      },
    }];

    if (quiz?.questions?.length > 0) {
      options.push({
        type: 'text',
        callback: openEditQuestions,
        text: t.LECTURE_PAGES.COMPONENTS.QUIZ.DROPDOWN.EDIT_QUESTIONS(),
        disabled: !!lecturePage.isLinked,
        tooltip: {
          enabled: !!lecturePage.isLinked,
          text: t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP(),
          placement: 'left',
          offset: 20,
        },
      });
    }

    if (!isCurrentCourseSelfPaced && !isUnlimitedAttempts) {
      options.push(
        { type: 'text',
          text: expirationDate ? t.LECTURE_PAGES.COMPONENTS.DROPDOWN.REMOVE_DEADLINE() : t.LECTURE_PAGES.COMPONENTS.DROPDOWN.ADD_DEADLINE(),
          disabled: course.isContentManagementCollection,
          tooltip: {
            enabled: course.isContentManagementCollection,
            text: t.LECTURE_PAGES.COMPONENTS.DROPDOWN.SETUP_IN_LINKED_LESSON_TOOLTIP(),
            placement: 'left',
            offset: 20,
          },
          callback: () => {
            saveComponent({
              expirationDate: expirationDate
                ? null
                : moment(currentLecture.releaseDate).add(1, 'weeks').endOf('day').toISOString(),
            });
          },
        },
      );
    }

    setSharedProps({
      ...initialSharedProps,
      extraOptions: {
        mode: 'prepend',
        renderOnMount: true,
        options,
      },
    });
  }, [
    saveComponent,
    setSharedProps,
    expirationDate,
    openEditQuestions,
    initialSharedProps,
    isCurrentCourseSelfPaced,
    currentLecture.releaseDate,
  ]);

  const closeQuestionModal = () => {
    forwardOnQuestionsModalCloseRef?.current?.callback();
    if (!forwardOnQuestionsModalCloseRef?.current?.restrictModalClose) {
      setShowEditQuestions(false);
    }
  };

  const handleAddQuestion = () => {
    setDisableAddQuestion(true);
    wrapThunkAction(dispatch(addQuizQuestion({
      questionSetId: quiz?.id,
      isRequired: true,
      questionType: QuizQuestionType.MULTIPLE_CHOICE,
      isAllQuestionViewQuiz: true,
    }))).then(() => {
      setShowEditQuestions(true);
    }).catch((e) => {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    }).finally(() => {
      setDisableAddQuestion(false);
    });
  };

  const getEmptyQuestionContainer = () => {
    if (mode === LecturePageMode.EDIT) {
      return (
        <div className='mt-6 p-2 d-flex flex-column need-questions align-items-center'>
          <p className='mb-0 text-small semi-bold'>{t.LECTURE_PAGES.COMPONENTS.QUIZ.ADD_QUESTIONS.TITLE()}</p>
          <p className='text-small'>{t.LECTURE_PAGES.COMPONENTS.QUIZ.ADD_QUESTIONS.DESCRIPTION()}</p>
          <Button
            variant='primary'
            onClick={handleAddQuestion}
            disabled={disableAddQuestion}
          >
            {t.LECTURE_PAGES.COMPONENTS.QUIZ.ADD_QUESTIONS.ADD()}
          </Button>
        </div>
      );
    }

    return (
      <div className='mt-6 py-5 px-1 d-flex flex-column text-gray-3 bg-gray-6 not-available-yet align-items-center'>
        <NvIcon className='' size='small' icon='ban' />
        <p className='mb-0 mt-2 text-small'>{t.LECTURE_PAGES.COMPONENTS.NOT_AVAILABLE_YET()}</p>
      </div>
    );
  };

  return (
    <div
      css={styles}
      ref={quizContainerRef}
      className='d-flex flex-column align-items-center pb-6'
    >
      <QuizComponentTitleContent
        type={ComponentType.QUIZ}
        quiz={quiz}
        mode={mode}
        currentLecture={currentLecture}
        saveComponent={saveComponent}
      />
      <FormProvider {...form}>
        <form className='w-100'>
          <div className='w-100 questions-list mt-6'>
            {questions.length === 0 && getEmptyQuestionContainer()}
            {questions?.map((question) => (
              <AllQuestionViewQuestion
                currentQuestion={question}
                quiz={quiz}
                mode={mode}
                lectureComponent={lectureComponent}
                showCorrectAnswers={showCorrectAnswers}
              />
            ))}
          </div>
          {!!quiz?.questions?.length && (
            <SubmitSection
              mode={mode}
              questionSetId={quiz.id}
              lectureComponent={lectureComponent}
              showCorrectAnswers={showCorrectAnswers}
              setShowCorrectAnswers={setShowCorrectAnswers}
              saveStatus={saveStatus}
              setSaveStatus={setSaveStatus}
              isContentManagementCollection={currentCourse.isContentManagementCollection}
              quizContainerRef={quizContainerRef}
            />
          )}
        </form>
      </FormProvider>
      <NvModal
        header={t.LECTURE_PAGES.COMPONENTS.QUIZ.MODAL.TITLE.EDIT()}
        show={showEditBasics}
        fullHeight={false}
        type={ModalType.DYNAMIC}
        onClose={() => setShowEditBasics(false)}
        body={(
          <AllQuestionViewQuizAttemptSettingModal
            isEdit
            currentAttempts={quiz.maximumAttempts}
            onCancel={() => setShowEditBasics(false)}
            onConfirm={async (payload) => {
              wrapThunkAction(dispatch(updateLectureComponent({
                catalogId: currentCatalogId,
                lecturePageId,
                componentData: {
                  id: lectureComponent.id,
                  type: lectureComponent.type,
                  ...payload,
                },
              }))).then(() => setShowEditBasics(false));
            }}
          />
        )}
      />
      <NvModal
        show={showEditQuestions}
        fullHeight={false}
        type={ModalType.NO_DIALOG}
        onClose={closeQuestionModal}
        body={(
          <div className='modal-page'>
            <QuizModal
              mode={0}
              closeModal={closeQuestionModal}
              reveal={false}
              lectureComponentId={lectureComponentId}
              questionIndex={0}
              isAllQuestionViewQuiz
              forwardOnQuestionsModalCloseRef={forwardOnQuestionsModalCloseRef}
            />
          </div>
        )}
      />
    </div>
  );
};

export default QuizLectureComponent;
